import { Input } from '@core-ui/Components/ui/input.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import {
  IconArrowRight,
  IconEdit,
  IconPlus,
  IconTrash,
  IconReload,
  IconDeviceFloppy
} from '@tabler/icons-react';
import { getCurrentUser } from '@features/auth';
import { type ApiResponse,
  type SelectOption,
  type IntegrationAccount,
  type IntegrationDisposition,
  type PatchMappingDto,
  type DispositionAbbreviation, type DispositionRecord,
  type HubspotFormattedDisposition
} from '@types';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect.tsx';
import { logError } from '@lib/logger/logError.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import { MappingDialog } from '@features/Settings/components/integrations/MappingDialog.tsx';
import { IntegrationMappingModalModes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { useHubspotApi } from '@features/Settings/hooks/useHubspotApi.ts';
import { useHubspotMappingsApi } from '@features/Settings/hooks/useHubspotMappingsApi.ts';
import { transformHubspotMappings } from '@utils/transformHubspotMappings.ts';

interface HubspotMappingsComponentProps {
    creationEnabled: boolean,
    editionEnabled: boolean,
    deletionEnabled: boolean,
}

export function HubspotMappingsComponent (
  {
    creationEnabled,
    editionEnabled,
    deletionEnabled
  }: HubspotMappingsComponentProps): React.JSX.Element {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();
  const { getAllHubspotAccounts } = useHubspotApi();
  const { getHubspotMappingsByOrgId, getHubspotCustomDispositions, patchHubspotMapping, deleteHubspotDisposition } = useHubspotMappingsApi();

  const [isSearchingMappings, setIsSearchingMappings] = useState<boolean>(false);
  const [customDispositions, setCustomDispositions] = useState<IntegrationDisposition[]>([]);
  const [mappings, setMappings] = useState<HubspotFormattedDisposition[]>([]);
  const [selectedDisposition, setSelectedDisposition] = useState('');
  const [selectedDispositionId, setSelectedDispositionId] = useState(0);
  const [mappingsForm, setMappingsForm] = useState<Map<DispositionAbbreviation, string>>(new Map<DispositionAbbreviation, string>);
  const [dispositionWasChanged, setDispositionWasChanged] = useState('' as DispositionAbbreviation);
  const [mappingValue, setMappingValue] = useState('');
  const nonce = useRef('');
  const completeMappings = useRef<HubspotFormattedDisposition[]>();
  const [tableKey, setTableKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [componentVersion, setComponentVersion] = useState(0);

  function findDispositionId (name: string): void {
    const disposition = customDispositions.find((disp)=>{
      return disp.attributes?.name === name;
    });
    setSelectedDispositionId(Number(disposition?.id));
  }

  function handleDispositionSearch (event: React.FormEvent<HTMLInputElement>): void {
    let search = '';
    if (event.target !== null) {
      search = (event.target as HTMLInputElement).value;
    }
    setIsSearchingMappings(search !== '');

    const filteredMappings = mappings
      .filter((mapping) =>
        mapping.defaultAction.toLowerCase().includes(search.toLowerCase())
      );

    setMappings(filteredMappings);
  }


  async function fetchDispositions (): Promise<IntegrationDisposition[] | undefined> {
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }
    const { userId, organizationId } = currentUser;

    const { data: accounts }: ApiResponse<IntegrationAccount[]> = await getAllHubspotAccounts(userId, organizationId);

    if (accounts !== undefined && accounts.length !== 0) {
      nonce.current = accounts[0].nonce;
      const { data } = await getHubspotCustomDispositions(organizationId, nonce.current);

      setCustomDispositions(data ?? []);
      return data;
    }
  }

  async function fetchMappings (): Promise<void> {
    if (currentUser === null) {
      throw new Error('No current user found in session store.');
    }

    const { organizationId } = currentUser;
    const { data: fetchedMappings } = await getHubspotMappingsByOrgId(organizationId);


    let callDispositions = customDispositions;

    if (customDispositions.length <= 0) {
      const data = await fetchDispositions();
      if (data !== undefined) {
        callDispositions = data;
      }


      if (fetchedMappings !== undefined && callDispositions.length !== 0) {
        const { id, mapping } = fetchedMappings;
        JSON.stringify(mapping);
        
        const transformedMapping = transformHubspotMappings(
          JSON.parse(mapping ) as DispositionRecord,
          callDispositions,
          id
        );

        completeMappings.current = transformedMapping;

        for (const formattedDisposition of transformedMapping) {
          mappingsForm.set(formattedDisposition.abbreviation, formattedDisposition.defaultName);
        }

        setMappings(transformedMapping);
      }
    }
  }

  async function patchMapping (): Promise<void> {
    if (mappings.length === 0 ) {
      throw new Error('No mappings found.');
    }

    const dispositionId = mappings[0].dispositionId;

    const patchMappingDto: PatchMappingDto = {
      mapping: JSON.stringify(Object.fromEntries(mappingsForm))
    };

    const response = await patchHubspotMapping(
      dispositionId,
      patchMappingDto
    );

    if (response === undefined) {
      await logError(
        t('settings.integrationsPages.mappings.patchErrorToast'),
        patchMapping.name,
        { dispositionId, patchMappingDto },
        true);
      return;
    }
    await logSuccess(
      t('settings.integrationsPages.mappings.patchSuccessToast'),
      patchMapping.name,
      { dispositionId, patchMappingDto },
      true);
  }

  function setUserPermissions (): void {
    if (currentUser !== null) {
      creationEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
      editionEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
      deletionEnabled = currentUser.isSuperAdmin || currentUser.isAdmin;
    }
  }

  const reload = async (): Promise<void> => {
    setIsLoading(true);
    setTableKey(prev => prev + 1);

    await fetchMappings();

    setIsLoading(false);
  };

  const remap = (): void => {
    setComponentVersion((prev) => prev + 1);
  };

  useEffect(()=> {
    setUserPermissions();
  }, [currentUser]);

  useEffect(() => {
    if (!isSearchingMappings) {
      void reload();
    }
  }, [isSearchingMappings, completeMappings]);

  return (
    <div className="grid items-center gap-2">
      <section title="mapping-controls" className="flex justify-start items-center gap-2">
        <form title="search-mapping-form" className="flex justify-start items-center gap-2 w-full">
          <Input
            id="mapping-search"
            className="w-1/2"
            placeholder={t('settings.integrationsPages.mappings.searchDispositionPlaceholder')}
            onInput={handleDispositionSearch}
            data-qa-id="search-mapping-input"
          ></Input>
          {/* TODO: Select by category when categories are defined */}
          <Button
            type="reset"
            variant="outline"
            onClick={()=>{
              setIsSearchingMappings(false);
              setMappings(completeMappings.current ?? []);
            }}
            data-qa-id="reset-mapping-search">{t('settings.integrationsPages.mappings.reset')}
          </Button>
        </form>
        {creationEnabled &&
                    <MappingDialog
                      trigger={<Button>
                        <IconPlus className="text-foreground"/>{t('settings.integrationsPages.mappings.createModalTitle')}
                      </Button>}
                      mode={IntegrationMappingModalModes.Create}
                      nonce={nonce.current}
                      refreshCallback={getHubspotMappingsByOrgId}
                    />}
        <Button
          disabled={selectedDisposition === ''}
          onClick={()=> {
            void patchMapping().then(()=> {
              setSelectedDisposition('');
            });
          }}
          data-qa-id="save-mapping-button"
        ><IconDeviceFloppy/>{t('settings.integrationsPages.mappings.saveDisposition')}</Button>
        <Button
          onClick={()=> {
            void reload();
            remap();
          }}
          disabled={isLoading}
          data-qa-id="sync-mapping-button"
        ><IconReload />{t('settings.integrationsPages.mappings.refreshMappings')}</Button>

      </section>
      <section key={componentVersion} title="mappings-listing">
        <div className="flex w-full overflow-y-auto mt-3">
          <Table key={tableKey} className='w-full overflow-y-auto'>
            <TableHeader className="hidden md:table-header-group sticky top-0">
              <TableRow>
                <TableHead className="text-foreground hover:bg-background p-0" dir="ltr">
                  <div className="rounded-l-md bg-muted p-2">
                    {t('settings.integrationsPages.mappings.table.tendrilDisposition')}
                  </div>
                </TableHead>
                <TableHead className="text-foreground p-0 text-left">
                  <div className="bg-muted p-2">
                    {t('settings.integrationsPages.mappings.table.mappedTo')}
                  </div>
                </TableHead>
                <TableHead className="text-foreground p-0 text-right" dir="rtl">
                  <div className="rounded-r-md bg-muted p-2 text-right">
                    {t('settings.integrationsPages.mappings.table.customDisposition')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="border-0">
              {mappings.map((mapping, index) => (
                <TableRow key={`mapping-row-${index}`}
                  className="block md:table-row border border-border/60 rounded-lg mb-2 p-2 md:border-0 md:rounded-none md:mb-0 md:p-0 md:border-t-0 hover:bg-transparent"
                >
                  <td className="flex justify-between items-center py-1.5 md:table-cell md:px-3 md:py-3 md:whitespace-nowrap">
                    <span className="text-xs font-medium text-muted-foreground md:hidden">
                      {t('settings.integrationsPages.mappings.table.tendrilDisposition')}
                    </span>
                    {mapping.defaultAction}
                  </td>
                  <td className="hidden md:table-cell px-3 py-3 text-center">
                    <IconArrowRight className="size-4 text-muted-foreground" />
                  </td>
                  <td className="text-xs font-medium text-muted-foreground ">
                    <span className="text-xs font-medium text-muted-foreground md:hidden">
                      {t('settings.integrationsPages.mappings.table.customDisposition')}
                    </span>
                    <div className="flex justify-end">
                      <div className="w-full md:w-3/4">
                        <InputSelect
                          placeholder={mapping.customName}
                          clearable={true}
                          options={customDispositions.reduce<SelectOption[]>((options: SelectOption[], customDisposition) => {
                            const customName = mapping.id !== String(customDisposition.id)
                              ? customDisposition.attributes.name
                              : mapping.customName;
                            const value = customDisposition.attributes.name;
                            options.push({ value, label: customName });
                            return options;
                          }, new Array<SelectOption>())
                          }
                          onValueChange={(val: string) => {
                            setSelectedDisposition(val);
                            const updatedMap = mappingsForm.set(mapping.abbreviation, val);
                            setMappingsForm(updatedMap);
                            setDispositionWasChanged(mapping.abbreviation);
                            setMappingValue(val);
                            findDispositionId(val);
                          }}
                          value={'' ?? mappingsForm?.get(mapping.abbreviation)}
                        >
                          {({
                            options,
                            placeholder,
                            clearable,
                            selectedValue,
                            isPopoverOpen,
                            setIsPopoverOpen,
                            onClearAllOptions,
                          }) => (
                            <InputSelectTrigger
                              options={options}
                              placeholder={placeholder}
                              clearable={clearable}
                              disabled={options === null}
                              selectedValue={selectedValue}
                              isPopoverOpen={isPopoverOpen}
                              setIsPopoverOpen={setIsPopoverOpen}
                              onClearAllOptions={onClearAllOptions}
                              className="w-full rounded-md px-3 py-2 text-left bg-muted hover:bg-muted/50 border mt-2 mb-2"
                            />
                          )}
                        </InputSelect>
                        {selectedDisposition !== '' && dispositionWasChanged === mapping.abbreviation &&
                                            <div className="flex gap-4">
                                              {editionEnabled &&
                                                    <MappingDialog
                                                      trigger={
                                                        <IconEdit className="text-foreground"/>}
                                                      mode={IntegrationMappingModalModes.Edit}
                                                      nonce={nonce.current}
                                                      currentDisposition={mapping}
                                                      dispositionId={selectedDispositionId}
                                                      refreshCallback={getHubspotMappingsByOrgId}
                                                      mappingValue = {mappingValue}
                                                    />}
                                              {deletionEnabled &&
                                                    <MappingDialog
                                                      trigger={<IconTrash className="text-destructive hover:text-foreground"/>}
                                                      nonce={nonce.current}
                                                      mode={IntegrationMappingModalModes.Delete}
                                                      dispositionId={selectedDispositionId}
                                                      refreshCallback={getHubspotMappingsByOrgId}
                                                      deleteCallback={deleteHubspotDisposition}
                                                    />}
                                            </div>
                        }
                      </div>
                    </div>
                  </td>
                </TableRow>
              ))}

            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  )
  ;
}